import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  AcademicCapIcon, 
  ClockIcon, 
  UserIcon, 
  StarIcon,
  PlayIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  BriefcaseIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const Courses = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();

  const courseCategories = [
    { name: 'All', icon: AcademicCapIcon },
    { name: 'Cybersecurity', icon: ShieldCheckIcon },
    { name: 'Development', icon: RocketLaunchIcon },
    { name: 'Business', icon: BriefcaseIcon },
    { name: 'Marketing', icon: SparklesIcon },
    { name: 'AI & ML', icon: BookOpenIcon }
  ];

  const courses = [
    {
      id: 1,
      title: "Hackers Library Bundle",
      category: "Cybersecurity",
      instructor: "Security Expert Team",
      duration: "40 hours",
      students: 44,
      rating: 3.5,
      price: 149,
      image: "/images/products/security/hackerofferbanner.jpeg",
      description: "Master cybersecurity fundamentals, ethical hacking, and penetration testing with hands-on labs.",
      features: [
        "40+ hours of video content",
        "Hands-on labs and exercises",
        "Real-world case studies",
        "Certification preparation",
        "Lifetime access",
        "Quantum cryptography",
        "Graphology & behavioral analysis"
      ],
      level: "Beginner to Advanced",
      isActive: true
    },
    {
      id: 2,
      title: "Python for Cybersecurity",
      category: "Development",
      instructor: "Python Security Pro",
      duration: "25 hours",
      students: 890,
      rating: 4.9,
      price: 999,
      originalPrice: 1999,
      image: "/images/products/security/python-security.webp",
      description: "Learn Python programming specifically for cybersecurity applications and automation.",
      features: [
        "25+ hours of video content",
        "Security-focused Python scripts",
        "Automation projects",
        "Network security tools",
        "Code review sessions"
      ],
      level: "Intermediate",
      isActive: false
    },
    {
      id: 3,
      title: "Advanced SOC Operations",
      category: "Cybersecurity",
      instructor: "SOC Specialist",
      duration: "35 hours",
      students: 650,
      rating: 4.7,
      price: 1299,
      originalPrice: 2499,
      image: "/images/products/security/soc-concepts.webp",
      description: "Deep dive into Security Operations Center concepts, threat hunting, and incident response.",
      features: [
        "35+ hours of advanced content",
        "SIEM implementation",
        "Threat hunting techniques",
        "Incident response procedures",
        "Team collaboration tools"
      ],
      level: "Advanced",
      isActive: false
    },
    {
      id: 4,
      title: "AI & Machine Learning Fundamentals",
      category: "AI & ML",
      instructor: "AI Research Team",
      duration: "30 hours",
      students: 2100,
      rating: 4.9,
      price: 1199,
      originalPrice: 2299,
      image: "/images/products/ai/ml-handbook.webp",
      description: "Comprehensive introduction to AI and machine learning with practical applications.",
      features: [
        "30+ hours of structured learning",
        "Hands-on ML projects",
        "TensorFlow and PyTorch",
        "Real-world applications",
        "Email support"
      ],
      level: "Beginner to Intermediate",
      isActive: false
    },
    {
      id: 5,
      title: "Web Development Masterclass",
      category: "Development",
      instructor: "Full Stack Developer",
      duration: "45 hours",
      students: 1800,
      rating: 4.8,
      price: 1399,
      originalPrice: 2799,
      image: "/images/products/web/templates.webp",
      description: "Complete web development course covering frontend, backend, and deployment.",
      features: [
        "45+ hours of comprehensive content",
        "Modern frameworks (React, Node.js)",
        "Database design",
        "Deployment strategies",
        "Portfolio projects"
      ],
      level: "Beginner to Advanced",
      isActive: false
    },
    {
      id: 6,
      title: "Digital Marketing Strategy",
      category: "Marketing",
      instructor: "Marketing Strategist",
      duration: "20 hours",
      students: 950,
      rating: 4.6,
      price: 899,
      originalPrice: 1799,
      image: "/images/products/marketing/marketing-plan.webp",
      description: "Strategic digital marketing course covering SEO, social media, and content marketing.",
      features: [
        "20+ hours of strategic content",
        "SEO optimization techniques",
        "Social media strategies",
        "Content marketing plans",
        "Analytics and reporting"
      ],
      level: "All Levels",
      isActive: false
    },
    {
      id: 7,
      title: "Business Analytics & Data Science",
      category: "Business",
      instructor: "Data Scientist",
      duration: "28 hours",
      students: 720,
      rating: 4.7,
      price: 1099,
      originalPrice: 2199,
      image: "/images/products/business/analytics-dashboard.webp",
      description: "Learn business analytics, data visualization, and decision-making with data.",
      features: [
        "28+ hours of analytics content",
        "Data visualization tools",
        "Statistical analysis",
        "Business intelligence",
        "Case studies"
      ],
      level: "Intermediate",
      isActive: false
    },
    {
      id: 8,
      title: "Ethical Hacking & Penetration Testing",
      category: "Cybersecurity",
      instructor: "Certified Ethical Hacker",
      duration: "50 hours",
      students: 580,
      rating: 4.9,
      price: 1699,
      originalPrice: 3499,
      image: "/images/products/security/zero-to-mastery.webp",
      description: "Comprehensive ethical hacking course with hands-on penetration testing labs.",
      features: [
        "50+ hours of intensive training",
        "Virtual lab environment",
        "Real-world scenarios",
        "Certification prep",
        "Career guidance"
      ],
      level: "Advanced",
      isActive: false
    }
  ];

  const filteredCourses = selectedCategory === 'All' 
    ? courses 
    : courses.filter(course => course.category === selectedCategory);

  const handleCourseClick = (course) => {
    if (course.isActive) {
      // For active courses, navigate to course detail page
      navigate(`/digital-bundle/${course.id}`);
    } else {
      // For coming soon courses, show notification
      toast.success(
        `🎓 "${course.title}" is coming soon! Stay tuned for updates.`,
        {
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #4ade80',
            borderRadius: '8px',
            padding: '16px',
          },
          iconTheme: {
            primary: '#4ade80',
            secondary: '#fff',
          },
        }
      );
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary-dark py-16 rounded-b-3xl">
        <div className="container">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-black mb-6">
              Master New Skills with Our
              <span className="text-primary"> Expert Courses</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Learn from industry experts with hands-on projects, real-world applications, and lifetime access to cutting-edge content.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-black">
              <div className="flex items-center gap-2">
                <AcademicCapIcon className="h-6 w-6 text-primary" />
                <span>1000+ Students Enrolled</span>
              </div>
              <div className="flex items-center gap-2">
                <StarIcon className="h-6 w-6 text-primary" />
                <span>4.8+ Average Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-6 w-6 text-primary" />
                <span>200+ Hours of Content</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="container py-8">
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {courseCategories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full border transition-all duration-300 ${
                selectedCategory === category.name
                  ? 'bg-primary text-black border-primary shadow-neon'
                  : 'bg-secondary text-gray-300 border-gray-600 hover:border-primary hover:text-primary'
              }`}
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course) => (
            <div 
              key={course.id} 
              className={`bg-secondary rounded-lg border border-primary/10 overflow-hidden hover:shadow-neon transition-all duration-300 group cursor-pointer relative ${
                !course.isActive ? 'blur-sm' : ''
              }`}
              onClick={() => handleCourseClick(course)}
            >
              {/* Coming Soon Overlay for inactive courses */}
              {!course.isActive && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="bg-primary text-black px-6 py-3 rounded-full text-lg font-bold mb-2 animate-pulse">
                      Coming Soon
                    </div>
                    <p className="text-black text-sm">Stay tuned for updates!</p>
                  </div>
                </div>
              )}
              
              {/* Course Image */}
              <div className="relative overflow-hidden">
                <img 
                  src={course.image} 
                  alt={course.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-black px-3 py-1 rounded-full text-sm font-medium">
                    {course.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-secondary text-black px-3 py-1 rounded-full text-sm font-medium">
                    {course.level}
                  </span>
                </div>
                {/* Status Badge */}
                <div className="absolute bottom-4 left-4">
                  {course.isActive ? (
                    <span className="bg-green-500 text-black px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                      Available Now
                    </span>
                  ) : (
                    <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>

              {/* Course Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-black mb-2 group-hover:text-primary transition-colors">
                  {course.title}
                </h3>
                <p className="text-gray-800 mb-4 line-clamp-2">
                  {course.description}
                </p>

                {/* Instructor */}
                <div className="flex items-center gap-2 mb-4">
                  <UserIcon className="h-4 w-4 text-primary" />
                  <span className="text-gray-300 text-sm">{course.instructor}</span>
                </div>

                {/* Course Stats */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4 text-sm text-gray-800">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <UserIcon className="h-4 w-4" />
                      <span>{course.students.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <StarIcon className="h-4 w-4 text-yellow-400" />
                      <span>{course.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-black font-semibold mb-2">What you'll learn:</h4>
                  <ul className="space-y-1">
                    {course.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-gray-800">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {feature}
                      </li>
                    ))}
                    <li className="flex items-center gap-2 text-sm text-primary">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      Instant Link Delivery
                    </li>
                    {course.features.length > 4 && (
                      <li className="flex items-center gap-2 text-sm text-primary">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        +{course.features.length - 4} more topics
                      </li>
                    )}
                  </ul>
                </div>

                {/* Price and CTA */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-primary">₹{course.price}</span>
                    {course.originalPrice && (
                      <span className="text-gray-700 line-through">₹{course.originalPrice}</span>
                    )}
                  </div>
                  {course.isActive ? (
                    <div className="flex items-center gap-2 bg-green-500 text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
                      <PlayIcon className="h-4 w-4" />
                      <span>Buy Now</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-lg">
                      <PlayIcon className="h-4 w-4" />
                      <span>Coming Soon</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-black mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-gray-800 mb-8 max-w-2xl mx-auto">
            Join thousands of students who have already transformed their careers with our expert-led courses.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-primary text-black px-8 py-3 rounded-lg hover:bg-primary/80 transition-colors text-lg font-semibold"
          >
            <BookOpenIcon className="h-5 w-5" />
            Browse All Products
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Courses; 